import React, { useEffect, useMemo, useRef, useState } from "react";
import Diagnosis from "./images/services/Diagnosis - Yaya Dental.jpeg";
import Implants from "./images/services/Implants - Yaya Dental.jpeg";
import Orthodontics from "./images/services/Orthodontics - Yaya Dental.jpeg";
import PediatricDentistry from "./images/services/Pediatric Dentistry - Yaya Dental.jpeg";
import Radiographs from "./images/services/Radiographs - Yaya Dental.jpeg";
import RootCanal from "./images/services/Root Canal - Yaya Dental.jpeg";
import TeethCleaning from "./images/services/Teeth Cleaning - Yaya Dental.jpeg";
import TeethFilling from "./images/services/Teeth Filling - Yaya Dental.jpeg";
import TeethWhitening from "./images/services/Teeth Whitening - Yaya Dental.jpeg";
import ToothExtraction from "./images/services/Tooth extraction - Yaya Dental.jpeg";

const CONTACT_EMAIL = "yayadentalclinic@gmail.com";
// WhatsApp booking number +254 723 934 728 (wa.me links use digits only, no "+")
const BOOKING_WHATSAPP = "254723934728";

// Every service renders as a card. Fields:
//   category   used by the filter chips
//   fit        optional: "contain" mats diagram-style images instead of cropping
//   bookingUrl optional: overrides the default WhatsApp booking deep-link
const SERVICES = [
  {
    id: "TeethWhitening",
    title: "Teeth Whitening",
    icon: "fa-wand-magic-sparkles",
    category: "Cosmetic",
    image: TeethWhitening,
    description:
      "Stains and discoloration of teeth are usually a result of our diet, " +
      "excess fluoride in the water we consume, or medications such as " +
      "tetracycline. Depending on the cause and severity, whitening can be " +
      "achieved through direct and indirect restorations and bleaching with " +
      "state of the art materials.",
  },
  {
    id: "Diagnosis",
    title: "Diagnosis",
    icon: "fa-stethoscope",
    category: "General",
    image: Diagnosis,
    description:
      "It's very important to visit a dentist twice every year, during which " +
      "a dental check-up is done and any issues identified are treated.",
  },
  {
    id: "Orthodontics",
    title: "Orthodontics",
    icon: "fa-teeth",
    category: "Specialist",
    image: Orthodontics,
    description:
      "Involves diagnosing, preventing and correcting teeth and jaws which " +
      "are not aligned properly. It is achieved using braces and clear " +
      "aligners. A diagnosis is made after a thorough dental and medical " +
      "history, analyzing study models, special x-rays and photographs.",
  },
  {
    id: "PediatricDentistry",
    title: "Pediatric Dentistry",
    icon: "fa-child",
    category: "Children",
    image: PediatricDentistry,
    description:
      "The earlier children are taught how to take care of their oral " +
      "health, the more unlikely they will depart from it. Diet counselling " +
      "is emphasized here. Premature loss of teeth, one of the main causes " +
      "of teeth misalignment, is also addressed. Close monitoring is " +
      "provided as the child transitions from milk teeth to permanent ones.",
  },
  {
    id: "Radiographs",
    title: "Radiographs",
    icon: "fa-x-ray",
    category: "General",
    image: Radiographs,
    description:
      "Dental radiographs are vital tools in aiding proper diagnosis for " +
      "correct treatment. Radiographs help assess the extent of teeth decay, " +
      "dental injuries and structures supporting the teeth. We have a state " +
      "of the art digital sensor for instant digital images, with a lead " +
      "apron for your protection.",
  },
  {
    id: "TeethFilling",
    title: "Teeth Filling",
    icon: "fa-fill-drip",
    category: "General",
    image: TeethFilling,
    description:
      "This concerns conservation of teeth in the mouth through direct or " +
      "indirect restoration of individual teeth. The defect on the teeth is " +
      "usually caused by dental caries (decay) or trauma. High quality " +
      "aesthetic materials are used to restore the teeth to function and " +
      "also restore aesthetics.",
  },
  {
    id: "RootCanal",
    title: "Root Canal",
    icon: "fa-tooth",
    category: "Specialist",
    fit: "contain",
    image: RootCanal,
    description:
      "This treatment helps you maintain your natural smile, continue eating " +
      "the foods you love and limits the need for ongoing dental work. With " +
      "proper care, most teeth that have had root canal treatment can last a " +
      "lifetime.",
  },
  {
    id: "TeethCleaning",
    title: "Teeth Cleaning",
    icon: "fa-hand-sparkles",
    category: "General",
    image: TeethCleaning,
    description:
      "In some quarters, this is also called gum treatment. It is very " +
      "important to have your teeth cleaned at least once a year, during " +
      "which tartar and plaque are removed and the teeth polished to restore " +
      "your smile. This ensures healthy gums.",
  },
  {
    id: "Implants",
    title: "Implants",
    icon: "fa-crown",
    category: "Surgical",
    image: Implants,
    description:
      "A dental implant is a component that is placed into the jaw bone " +
      "beneath the gum to allow placement of a dental prosthesis like a " +
      "crown or bridge. Currently this is the most modern method worldwide " +
      "of replacing missing tooth/teeth. We were not left behind!",
  },
  {
    id: "ToothExtraction",
    title: "Tooth Extraction",
    icon: "fa-teeth-open",
    category: "Surgical",
    image: ToothExtraction,
    description:
      "We do dental extractions for both adults and children as atraumatic " +
      "as possible. Disimpaction of wisdom teeth and also other commonly " +
      "impacted teeth like canines are also performed at our clinic. We " +
      "manage dental infections like abscess, dry socket and also perform " +
      "minor oral surgeries.",
  },
];

// Per-service booking link. Falls back to a pre-filled WhatsApp message on
// the clinic's booking line; set bookingUrl on any service once an online
// booking system is available and it takes precedence automatically.
const bookingHref = (service) =>
  service.bookingUrl ||
  `https://wa.me/${BOOKING_WHATSAPP}?text=${encodeURIComponent(
    `Hello Yaya Dental Clinic, I would like to book an appointment for ${service.title}.`
  )}`;

const emailHref = (service) =>
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    `Appointment request: ${service.title}`
  )}`;

const CATEGORIES = ["All", ...new Set(SERVICES.map((s) => s.category))];

// Structured data (schema.org) built from the same array as the cards, so
// search engines can surface the clinic and every service it offers.
const clinicSchema = {
  "@context": "https://schema.org",
  "@type": "Dentist",
  name: "Yaya Dental Clinic",
  email: CONTACT_EMAIL,
  telephone: "+254723934728",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Yaya Center, 3rd Floor, Suite Number 8",
    addressLocality: "Nairobi",
    addressCountry: "KE",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "18:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Dental Services",
    itemListElement: SERVICES.map((s) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "MedicalProcedure",
        name: s.title,
        description: s.description,
      },
    })),
  },
};

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&family=Young+Serif&display=swap");

.sv {
  --sv-ink: #10303a;
  --sv-body: #3f5a62;
  --sv-teal: #0d7a85;
  --sv-teal-dark: #0a5f68;
  --sv-mist: #eaf4f3;
  --sv-line: #d3e5e4;
  --sv-wa: #1a9a5a;
  --sv-wa-dark: #147a47;
  font-family: "Figtree", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: var(--sv-body);
  background: linear-gradient(180deg, #ffffff 0%, var(--sv-mist) 100%);
  padding: clamp(3.5rem, 9vw, 7rem) clamp(1rem, 4vw, 2.5rem) clamp(3rem, 7vw, 5rem);
}
.sv *, .sv *::before, .sv *::after { box-sizing: border-box; }
.sv-wrap { max-width: 74rem; margin: 0 auto; }

.sv-head { max-width: 42rem; margin: 0 auto clamp(1.75rem, 4vw, 2.75rem); text-align: center; }
.sv-title {
  font-family: "Young Serif", Georgia, serif;
  font-weight: 400;
  color: var(--sv-ink);
  font-size: clamp(1.9rem, 1.2rem + 3.2vw, 3.1rem);
  line-height: 1.1;
  letter-spacing: -0.01em;
  margin: 0 0 1rem;
  text-wrap: balance;
}
.sv-lede { font-size: clamp(1rem, 0.95rem + 0.25vw, 1.15rem); line-height: 1.65; margin: 0; text-wrap: pretty; }

/* Toolbar */
.sv-tools { display: flex; flex-direction: column; gap: 1rem; align-items: center; margin-bottom: clamp(1.5rem, 4vw, 2.5rem); }
.sv-search { position: relative; width: 100%; max-width: 26rem; }
.sv-search i { position: absolute; left: 1.1rem; top: 50%; transform: translateY(-50%); color: var(--sv-teal); pointer-events: none; }
.sv-search input {
  width: 100%;
  font: inherit;
  color: var(--sv-ink);
  background: #fff;
  border: 1.5px solid var(--sv-line);
  border-radius: 999px;
  padding: 0.8rem 1.25rem 0.8rem 2.9rem;
  min-height: 3rem;
}
.sv-search input::placeholder { color: #7d9399; }
.sv-search input:focus { outline: none; border-color: var(--sv-teal); box-shadow: 0 0 0 3px rgba(13, 122, 133, 0.18); }

.sv-chips {
  display: flex;
  gap: 0.5rem;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.25rem 0.25rem 0.5rem;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.sv-chips::-webkit-scrollbar { display: none; }
@media (min-width: 48rem) { .sv-chips { flex-wrap: wrap; justify-content: center; overflow: visible; } }

.sv-chip {
  flex: none;
  font: inherit;
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--sv-teal-dark);
  background: #fff;
  border: 1.5px solid var(--sv-line);
  border-radius: 999px;
  padding: 0.5rem 1.1rem;
  min-height: 2.5rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.sv-chip:hover { border-color: var(--sv-teal); }
.sv-chip[aria-pressed="true"], .sv-chip.is-active { background: var(--sv-teal); border-color: var(--sv-teal); color: #fff; }

.sv-count { margin: 0; font-size: 0.9rem; }

/* Grid */
.sv-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: clamp(1rem, 2.5vw, 1.75rem);
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 19rem), 1fr));
}
.sv-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid var(--sv-line);
  border-radius: 1.25rem;
  overflow: hidden;
  transition: border-color 0.2s ease;
}
.sv-card:hover { border-color: var(--sv-teal); }

.sv-media { position: relative; aspect-ratio: 16 / 10; background: var(--sv-mist); overflow: hidden; }
.sv-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sv-media img.is-contain { object-fit: contain; padding: 0.5rem; background: #fff; }
.sv-tag {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--sv-ink);
  background: rgba(255, 255, 255, 0.94);
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
}
.sv-icon {
  position: absolute;
  right: 0.75rem;
  bottom: -1.25rem;
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--sv-teal);
  color: #fff;
  border: 3px solid #fff;
  font-size: 1rem;
  z-index: 1;
}
.sv-media { overflow: visible; }
.sv-media-inner { position: absolute; inset: 0; overflow: hidden; }

.sv-body { display: flex; flex-direction: column; gap: 0.5rem; padding: 1.4rem 1.35rem 1.4rem; flex: 1; }
.sv-card-title { margin: 0; font-size: 1.2rem; font-weight: 600; color: var(--sv-ink); line-height: 1.3; padding-right: 2rem; }
.sv-excerpt {
  margin: 0 0 0.75rem;
  line-height: 1.6;
  font-size: 0.97rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.sv-more {
  margin-top: auto;
  align-self: flex-start;
  font: inherit;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--sv-teal-dark);
  background: transparent;
  border: 1.5px solid var(--sv-teal);
  border-radius: 999px;
  padding: 0.6rem 1.15rem;
  min-height: 2.75rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}
.sv-more:hover { background: var(--sv-teal); color: #fff; }

.sv :is(.sv-more, .sv-chip, .sv-close, .sv-wa, .sv-mail):focus-visible { outline: 3px solid #f0b429; outline-offset: 3px; }

/* Empty */
.sv-empty { text-align: center; padding: 3rem 1rem; }
.sv-empty i { font-size: 2rem; color: var(--sv-teal); margin-bottom: 0.75rem; }
.sv-empty h2 { font-size: 1.25rem; color: var(--sv-ink); margin: 0 0 0.35rem; }
.sv-empty p { margin: 0 0 1.25rem; }

/* Dialog */
.sv-overlay {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(10, 35, 42, 0.6);
  animation: sv-fade 0.18s ease-out;
}
.sv-dialog {
  position: relative;
  width: 100%;
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.75rem 1.4rem calc(1.75rem + env(safe-area-inset-bottom, 0px));
  animation: sv-rise 0.22s ease-out;
}
.sv-dlg-head { display: flex; align-items: center; gap: 0.85rem; margin: 0 3rem 1rem 0; }
.sv-dlg-icon { flex: none; width: 2.75rem; height: 2.75rem; display: grid; place-items: center; border-radius: 50%; background: var(--sv-mist); color: var(--sv-teal-dark); }
.sv-dlg-title { font-family: "Young Serif", Georgia, serif; font-weight: 400; font-size: clamp(1.3rem, 1rem + 1vw, 1.7rem); color: var(--sv-ink); margin: 0; line-height: 1.2; }
.sv-dlg-tag { margin: 0.15rem 0 0; font-size: 0.9rem; color: var(--sv-teal-dark); }
.sv-dlg-text { margin: 0; line-height: 1.7; max-width: 60ch; text-wrap: pretty; }
.sv-actions { display: grid; gap: 0.75rem; margin-top: 1.5rem; }
.sv-wa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 3rem;
  padding: 0.7rem 1.25rem;
  border-radius: 999px;
  background: var(--sv-wa);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease;
}
.sv-wa:hover { background: var(--sv-wa-dark); color: #fff; }
.sv-mail { text-align: center; color: var(--sv-teal-dark); font-weight: 500; padding: 0.5rem; text-decoration: underline; text-underline-offset: 3px; }
.sv-close {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  width: 2.75rem;
  height: 2.75rem;
  border: 0;
  border-radius: 50%;
  background: var(--sv-mist);
  color: var(--sv-ink);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}
.sv-close:hover { background: var(--sv-line); }

@media (min-width: 40rem) {
  .sv-overlay { align-items: center; padding: 1.5rem; }
  .sv-dialog { max-width: 34rem; border-radius: 1.5rem; padding: 2rem; }
}

@keyframes sv-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes sv-rise { from { transform: translateY(1.5rem); opacity: 0; } to { transform: none; opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .sv-overlay, .sv-dialog { animation: none; }
  .sv-card, .sv-more, .sv-chip, .sv-wa { transition: none; }
}
`;

export default function Services() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const closeRef = useRef(null);
  const triggerRef = useRef(null);

  const filteredServices = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICES.filter((s) => {
      const inCategory = category === "All" || s.category === category;
      const matches =
        q === "" ||
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q);
      return inCategory && matches;
    });
  }, [category, query]);

  const clearFilters = () => {
    setCategory("All");
    setQuery("");
  };

  const openService = (service, e) => {
    triggerRef.current = e.currentTarget;
    setActive(service);
  };

  useEffect(() => {
    if (!active) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      triggerRef.current?.focus();
    };
  }, [active]);

  return (
    <section className="sv" id="services" aria-labelledby="sv-title">
      <style>{CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(clinicSchema) }}
      />

      <div className="sv-wrap">
        <header className="sv-head">
          <h1 id="sv-title" className="sv-title">Our services</h1>
          <p className="sv-lede">
            Experience exceptional dental care at our clinic. Trust us for
            gentle care, personalized attention, and a smile that shines.
          </p>
        </header>

        <div className="sv-tools">
          <div className="sv-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Search services"
              aria-label="Search services"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="sv-chips" role="group" aria-label="Filter services by category">
            {CATEGORIES.map((name) => (
              <button
                key={name}
                type="button"
                className="sv-chip"
                aria-pressed={category === name}
                onClick={() => setCategory(name)}
              >
                {name}
              </button>
            ))}
          </div>

          <p className="sv-count" aria-live="polite">
            {filteredServices.length === SERVICES.length
              ? `${SERVICES.length} services`
              : `${filteredServices.length} of ${SERVICES.length} services`}
          </p>
        </div>

        {filteredServices.length === 0 ? (
          <div className="sv-empty">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <h2>No services match your search</h2>
            <p>Try a different keyword or category.</p>
            <button type="button" className="sv-chip is-active" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        ) : (
          <ul className="sv-grid">
            {filteredServices.map((service) => (
              <li className="sv-card" key={service.id}>
                <div className="sv-media">
                  <div className="sv-media-inner">
                    <img
                      src={service.image}
                      className={service.fit === "contain" ? "is-contain" : undefined}
                      alt={`${service.title} at Yaya Dental Clinic`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <span className="sv-tag">{service.category}</span>
                  <span className="sv-icon" aria-hidden="true">
                    <i className={`fa-solid ${service.icon}`}></i>
                  </span>
                </div>

                <div className="sv-body">
                  <h2 className="sv-card-title">{service.title}</h2>
                  <p className="sv-excerpt">{service.description}</p>
                  <button
                    type="button"
                    className="sv-more"
                    onClick={(e) => openService(service, e)}
                    aria-haspopup="dialog"
                    aria-label={`Learn more about ${service.title}`}
                  >
                    Learn more
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {active && (
        <div
          className="sv-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setActive(null);
          }}
        >
          <div className="sv-dialog" role="dialog" aria-modal="true" aria-labelledby="sv-dlg-title">
            <button
              ref={closeRef}
              type="button"
              className="sv-close"
              onClick={() => setActive(null)}
              aria-label="Close"
            >
              &times;
            </button>

            <div className="sv-dlg-head">
              <span className="sv-dlg-icon" aria-hidden="true">
                <i className={`fa-solid ${active.icon}`}></i>
              </span>
              <div>
                <h2 id="sv-dlg-title" className="sv-dlg-title">{active.title}</h2>
                <p className="sv-dlg-tag">{active.category}</p>
              </div>
            </div>

            <p className="sv-dlg-text">{active.description}</p>

            <div className="sv-actions">
              <a className="sv-wa" href={bookingHref(active)} target="_blank" rel="noopener noreferrer">
                <i className="fa-brands fa-whatsapp" aria-hidden="true"></i>
                Book via WhatsApp
              </a>
              <a className="sv-mail" href={emailHref(active)}>
                Prefer email? Book here
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}